import React, { useEffect, useMemo, useState } from 'react';
import type {
  PaystackSubaccountDetails,
  PaystackSubaccountStatus,
  PaystackSubaccountUpdatePayload,
} from '../../types/admin';

const PAYSTACK_STATUS_OPTIONS: PaystackSubaccountStatus[] = [
  'NOT_CREATED',
  'CREATED',
  'UNVERIFIED',
  'VERIFIED',
  'REQUIRES_UPDATE',
  'DISABLED',
  'FAILED',
];

type PaystackFormState = {
  paystackSubaccountCode: string;
  paystackSubaccountId: string;
  paystackSubaccountStatus: PaystackSubaccountStatus;
  paystackSplitPercentage: string;
  paystackVerifiedAt: string;
  paystackCreatedAt: string;
  paystackLastSyncAt: string;
};

interface PaystackSubaccountSetupProps {
  copy?: string;
  disabled?: boolean;
  embedded?: boolean;
  onSave: (payload: PaystackSubaccountUpdatePayload) => Promise<void>;
  source?: PaystackSubaccountDetails | null;
  title?: string;
}

function toDateTimeInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(' ', 'T').slice(0, 16);
}

function toCurrentDateTimeInputValue(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toInitialState(source?: PaystackSubaccountDetails | null): PaystackFormState {
  return {
    paystackSubaccountCode: source?.paystackSubaccountCode ?? '',
    paystackSubaccountId: source?.paystackSubaccountId ?? '',
    paystackSubaccountStatus: PAYSTACK_STATUS_OPTIONS.includes(source?.paystackSubaccountStatus as PaystackSubaccountStatus)
      ? (source?.paystackSubaccountStatus as PaystackSubaccountStatus)
      : 'NOT_CREATED',
    paystackSplitPercentage:
      source?.paystackSplitPercentage === null || source?.paystackSplitPercentage === undefined
        ? '85.00'
        : String(source.paystackSplitPercentage),
    paystackVerifiedAt: toDateTimeInputValue(source?.paystackVerifiedAt),
    paystackCreatedAt: toDateTimeInputValue(source?.paystackCreatedAt),
    paystackLastSyncAt: toDateTimeInputValue(source?.paystackLastSyncAt),
  };
}

function validatePaystackForm(form: PaystackFormState): string {
  const status = form.paystackSubaccountStatus;
  const code = form.paystackSubaccountCode.trim();
  const splitPercentage = Number.parseFloat(form.paystackSplitPercentage);

  if (status !== 'NOT_CREATED' && !code) {
    return 'Paystack subaccount code is required unless status is NOT CREATED.';
  }

  if (status === 'VERIFIED' && !code) {
    return 'Verified Paystack setup requires an ACCT_ subaccount code.';
  }

  if (code && !code.startsWith('ACCT_')) {
    return 'Paystack subaccount code must start with ACCT_.';
  }

  if (status === 'VERIFIED' && !form.paystackVerifiedAt) {
    return 'Verified timestamp is required when Paystack status is VERIFIED.';
  }

  if (!Number.isFinite(splitPercentage) || splitPercentage < 0 || splitPercentage > 100) {
    return 'Split percentage must be between 0 and 100.';
  }

  if (status === 'VERIFIED' && splitPercentage <= 0) {
    return 'Verified Paystack setup requires a split percentage greater than 0 and no more than 100.';
  }

  return '';
}

const PaystackSubaccountSetup: React.FC<PaystackSubaccountSetupProps> = ({
  copy = 'Save the Paystack Dashboard subaccount details used for split settlement.',
  disabled = false,
  embedded = false,
  onSave,
  source,
  title = 'Paystack subaccount setup',
}) => {
  const [form, setForm] = useState<PaystackFormState>(() => toInitialState(source));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setForm(toInitialState(source));
    setError('');
    setSuccess('');
  }, [source]);

  const currentStatus = form.paystackSubaccountStatus || 'NOT_CREATED';
  const statusClassName = useMemo(
    () => `admin-pill${currentStatus === 'VERIFIED' ? ' admin-pill-success' : ''}`,
    [currentStatus],
  );

  const setField = (field: keyof PaystackFormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const nextValue = event.target.value;
    setForm((current) => {
      const next = { ...current, [field]: nextValue };
      if (
        field === 'paystackSubaccountStatus' &&
        nextValue === 'VERIFIED' &&
        !current.paystackVerifiedAt
      ) {
        next.paystackVerifiedAt = toCurrentDateTimeInputValue();
      }
      return next;
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationMessage = validatePaystackForm(form);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await onSave({
        paystackSubaccountCode: form.paystackSubaccountCode,
        paystackSubaccountId: form.paystackSubaccountId,
        paystackSubaccountStatus: form.paystackSubaccountStatus,
        paystackSplitPercentage: Number.parseFloat(form.paystackSplitPercentage),
        paystackVerifiedAt: form.paystackVerifiedAt,
        paystackCreatedAt: form.paystackCreatedAt,
        paystackLastSyncAt: form.paystackLastSyncAt,
      });
      setSuccess('Paystack setup saved.');
    } catch (saveError: any) {
      setError(saveError.message || 'Could not save Paystack setup.');
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">{title}</h2>
          <p className="admin-panel-copy">{copy}</p>
          <p className="admin-panel-copy">
            Create the subaccount in Paystack Dashboard first, then paste the ACCT_ code here. ShiftWagon uses this code when authorizing bookings so Paystack can split settlement automatically.
          </p>
        </div>
        <div className="admin-pill-row">
          <span className={statusClassName}>{currentStatus.replaceAll('_', ' ')}</span>
        </div>
      </div>

      <form className="register-form" onSubmit={handleSubmit}>
        <div className="admin-detail-grid paystack-subaccount-grid">
          <div className="register-field">
            <label className="register-label" htmlFor="paystackSubaccountCode">Subaccount code</label>
            <input
              className="register-input"
              id="paystackSubaccountCode"
              onChange={setField('paystackSubaccountCode')}
              placeholder="ACCT_xxxxx"
              value={form.paystackSubaccountCode}
            />
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="paystackSubaccountId">Subaccount ID</label>
            <input
              className="register-input"
              id="paystackSubaccountId"
              onChange={setField('paystackSubaccountId')}
              placeholder="Optional Paystack ID"
              value={form.paystackSubaccountId}
            />
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="paystackSubaccountStatus">Status</label>
            <select
              className="register-input register-select"
              id="paystackSubaccountStatus"
              onChange={setField('paystackSubaccountStatus')}
              value={form.paystackSubaccountStatus}
            >
              {PAYSTACK_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="paystackSplitPercentage">Split percentage</label>
            <input
              className="register-input"
              id="paystackSplitPercentage"
              max="100"
              min="0"
              onChange={setField('paystackSplitPercentage')}
              step="0.01"
              type="number"
              value={form.paystackSplitPercentage}
            />
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="paystackVerifiedAt">Verified at</label>
            <input
              className="register-input"
              id="paystackVerifiedAt"
              onChange={setField('paystackVerifiedAt')}
              type="datetime-local"
              value={form.paystackVerifiedAt}
            />
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="paystackCreatedAt">Created at</label>
            <input
              className="register-input"
              id="paystackCreatedAt"
              onChange={setField('paystackCreatedAt')}
              type="datetime-local"
              value={form.paystackCreatedAt}
            />
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="paystackLastSyncAt">Last sync at</label>
            <input
              className="register-input"
              id="paystackLastSyncAt"
              onChange={setField('paystackLastSyncAt')}
              type="datetime-local"
              value={form.paystackLastSyncAt}
            />
          </div>
        </div>

        {error ? <div className="register-inline-error">{error}</div> : null}
        {success ? <div className="register-inline-success">{success}</div> : null}
        <div className="admin-inline-actions" style={{ marginTop: '14px' }}>
          <button
            className="register-submit register-submit-inline"
            disabled={disabled || saving}
            type="submit"
          >
            {saving ? 'Saving...' : 'Save Paystack setup'}
          </button>
          <span className="admin-muted-note">
            Verified Paystack setup is required before payout-relevant approvals can complete.
          </span>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return <div>{content}</div>;
  }

  return <section className="admin-panel">{content}</section>;
};

export default PaystackSubaccountSetup;
